// Dialogue UI rendered as DOM (not canvas) so it's accessible and easy to style.
// Supports three shapes: plain lines (dismiss to continue), name input, and
// branching choices.

export class DialogueUI {
  constructor(root) {
    this.root = root;
    this.current = null;
    this.resolver = null;
  }

  _format(text, ctx) {
    return text.replace(/\{(\w+)\}/g, (_, key) => ctx[key] ?? "");
  }

  async present(node, ctx = {}) {
    // Reset and show the overlay shell.
    this.root.innerHTML = "";
    this.root.classList.remove("hidden");

    const box = document.createElement("div");
    box.className = "tavern-dialogue-box";

    // Lines of dialogue.
    for (const line of node.lines) {
      const p = document.createElement("p");
      p.textContent = this._format(line, ctx);
      box.appendChild(p);
    }

    // Shape 1: awaits a name input.
    if (node.awaits_input === "name") {
      const input = document.createElement("input");
      input.type = "text";
      input.maxLength = 24;
      input.placeholder = "Your name";
      input.className = "tavern-dialogue-input";
      const submit = document.createElement("button");
      submit.textContent = "Tell them";
      submit.className = "tavern-dialogue-button";
      box.appendChild(input);
      box.appendChild(submit);
      this.root.appendChild(box);

      return new Promise((resolve) => {
        const finish = () => {
          const value = input.value.trim();
          if (value.length === 0) { input.focus(); return; }
          this.close();
          resolve({ type: "name", value });
        };
        submit.addEventListener("click", finish);
        input.addEventListener("keydown", (e) => { if (e.key === "Enter") finish(); });
        setTimeout(() => input.focus(), 50);
      });
    }

    // Shape 2: branching choices.
    if (Array.isArray(node.choices) && node.choices.length > 0) {
      const choices = document.createElement("div");
      choices.className = "tavern-dialogue-choices";
      for (const [i, choice] of node.choices.entries()) {
        const btn = document.createElement("button");
        btn.textContent = choice.label;
        btn.className = "tavern-dialogue-button";
        btn.dataset.index = String(i);
        choices.appendChild(btn);
      }
      box.appendChild(choices);
      this.root.appendChild(box);

      return new Promise((resolve) => {
        choices.addEventListener("click", (e) => {
          const btn = e.target.closest("button");
          if (!btn) return;
          const choice = node.choices[Number(btn.dataset.index)];
          this.close();
          resolve({ type: "choice", effect: choice.effect, next: choice.next, label: choice.label });
        });
      });
    }

    // Shape 3: plain lines with a dismiss button.
    const dismiss = document.createElement("button");
    dismiss.textContent = "...";
    dismiss.className = "tavern-dialogue-button";
    box.appendChild(dismiss);
    this.root.appendChild(box);

    return new Promise((resolve) => {
      dismiss.addEventListener("click", () => {
        this.close();
        resolve({ type: "dismiss" });
      });
    });
  }

  close() {
    this.root.classList.add("hidden");
    this.root.innerHTML = "";
  }
}
